import React, {useEffect, useState, useRef} from 'react'
import AdyenCheckout from '@adyen/adyen-web'
import '@adyen/adyen-web/dist/adyen.css'
import {Box, Flex, Spinner} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getCurrencyValueForApi} from '../utils/parsers.js'
import {AdyenPaymentsService} from '../services/payments.js'
import {AdyenShippingMethodsService} from '../services/shipping-methods.js'
import useAdyenExpressCheckout from '../hooks/useAdyenExpressCheckout.js'
import useAdyenCheckout from '../hooks/useAdyenCheckout.js'
import {AdyenShippingAddressService} from '../services/shipping-address.js'
import {useStoreContext} from '../../../contexts/store.jsx'
import {formatNames} from '../../../api/utils/formatAddress.js'
import useTagManager from '../../../hooks/use-tag-manager.js'
import {TWO_DAY_METHOD_ID} from '../../../constants.js'
import {isFineJewelryExists} from '../../../utils/basket-utils.js'
export const getApplePaymentMethodConfig = (paymentMethodsResponse) => {
    const applePayPaymentMethod = paymentMethodsResponse?.paymentMethods?.find(
        (pm) => pm.type === 'applepay'
    )
    return applePayPaymentMethod?.configuration || null
}

export const getApplePayFormattedNames = (firstName, lastName) => {
    let firstNameFormatted, lastNameFormatted
    const firstNameValid = firstName && firstName.length > 1
    const lastNameValid = lastName && lastName.length > 1

    if (firstNameValid && lastNameValid) {
        firstNameFormatted = firstName
        lastNameFormatted = lastName
    }

    if (firstNameValid && !lastNameValid) {
        const splitNames =
            firstName.split(' ').length > 1
                ? firstName.split(' ')
                : firstName.split('.').length > 1
                ? firstName.split('.')
                : null
        if (splitNames) {
            firstNameFormatted = splitNames[0]
            lastNameFormatted = splitNames[1]
        } else {
            firstNameFormatted = firstName
            lastNameFormatted = '.'
        }
    }

    firstNameFormatted = formatNames(firstNameFormatted)
    lastNameFormatted = formatNames(lastNameFormatted)

    return {firstNameFormatted, lastNameFormatted}
}

export const getCustomerShippingDetails = (shippingContact, isBopis, storeAddress) => {
    const {firstNameFormatted, lastNameFormatted} = getApplePayFormattedNames(
        shippingContact.givenName,
        shippingContact.familyName
    )

    if (isBopis && storeAddress) {
        return {
            deliveryAddress: {
                city: storeAddress.city,
                fullName: storeAddress.name,
                country:
                    storeAddress.countryCode === 'United States' ? 'US' : storeAddress.countryCode,
                houseNumberOrName: storeAddress.address2 || '',
                postalCode: storeAddress.postalCode,
                stateOrProvince: storeAddress.stateCode,
                street: storeAddress.address1
            },
            profile: {
                firstName: firstNameFormatted || '.',
                lastName: lastNameFormatted || '.',
                email: shippingContact.emailAddress,
                phone: shippingContact.phoneNumber
            }
        }
    } else {
        return {
            deliveryAddress: {
                city: shippingContact.locality,
                country: shippingContact.countryCode,
                houseNumberOrName:
                    shippingContact.addressLines?.length > 1 ? shippingContact.addressLines[1] : '',
                postalCode: shippingContact.postalCode,
                stateOrProvince: shippingContact.administrativeArea,
                street: shippingContact.addressLines?.[0]
            },
            profile: {
                firstName: firstNameFormatted || 'Customer',
                lastName: lastNameFormatted || '.',
                email: shippingContact.emailAddress,
                phone: shippingContact.phoneNumber
            }
        }
    }
}

export const getCustomerBillingDetails = (billingContact) => {
    const {firstNameFormatted, lastNameFormatted} = getApplePayFormattedNames(
        billingContact.givenName,
        billingContact.familyName
    )
    return {
        billingAddress: {
            firstName: firstNameFormatted,
            lastName: lastNameFormatted,
            city: billingContact.locality,
            country: billingContact.countryCode,
            houseNumberOrName:
                billingContact?.addressLines?.length > 1 ? billingContact.addressLines[1] : '',
            postalCode: billingContact.postalCode,
            stateOrProvince: billingContact.administrativeArea,
            street: billingContact.addressLines?.[0]
        }
    }
}

export const getAppleButtonConfig = (
    authToken,
    site,
    basket,
    shippingMethods,
    applePayConfig,
    navigate,
    fetchShippingMethods,
    isBopis = false,
    store
) => {
    let applePayAmount = basket.orderTotal
    const mappedShippingMethods = shippingMethods?.map((sm) => ({
        label: sm.name,
        detail: sm.description,
        identifier: sm.id,
        amount: `${sm.price}`,
        c_storePickupEnabled: sm.c_storePickupEnabled
    }))

    const buttonConfig = {
        showPayButton: true,
        isExpress: true,
        configuration: applePayConfig,
        amount: {
            value: getCurrencyValueForApi(basket.orderTotal, basket.currency),
            currency: basket.currency
        },
        shippingMethods: isBopis
            ? mappedShippingMethods?.filter((ship) => ship.c_storePickupEnabled)
            : mappedShippingMethods,
        requiredShippingContactFields: isBopis
            ? ['name', 'email', 'phone']
            : ['postalAddress', 'name', 'email', 'phone'],
        requiredBillingContactFields: ['name', 'postalAddress', 'phone'],
        onAuthorized: async (resolve, reject, event) => {
            try {
                const {shippingContact, billingContact, token} = event.payment
                const state = {
                    data: {
                        paymentType: 'express',
                        paymentMethod: {
                            type: 'applepay',
                            applePayToken: token.paymentData
                        },
                        additionalPaymentData: {
                            applepay: {
                                paymentNetwork: token?.paymentMethod?.network,
                                paymentDisplayName: token?.paymentMethod?.displayName
                            }
                        },
                        ...getCustomerBillingDetails(billingContact),
                        ...getCustomerShippingDetails(shippingContact, isBopis, store)
                    }
                }
                const adyenPaymentService = new AdyenPaymentsService(authToken, site)
                const paymentsResponse = await adyenPaymentService.submitPayment(
                    {
                        ...state.data,
                        origin: state.data.origin ? state.data.origin : window.location.origin
                    },
                    basket?.basketId,
                    basket?.customerInfo?.customerId,
                    true,
                    applePayAmount
                )
                if (paymentsResponse?.isFinal && paymentsResponse?.isSuccessful) {
                    const finalPriceUpdate = {
                        newTotal: {
                            type: 'final',
                            label: applePayConfig.merchantName,
                            amount: `${applePayAmount}`
                        }
                    }
                    resolve(finalPriceUpdate)
                    navigate(`/checkout/confirmation/${paymentsResponse?.merchantReference}`)
                } else {
                    reject()
                }
            } catch (err) {
                reject()
            }
        },
        onSubmit: () => {},
        onShippingContactSelected: async (resolve, reject, event) => {
            try {
                let basketResponse = basket
                const {shippingContact} = event
                const adyenShippingAddressService = new AdyenShippingAddressService(authToken, site)
                const adyenShippingMethodsService = new AdyenShippingMethodsService(authToken, site)
                let customerShippingDetails = getCustomerShippingDetails(shippingContact)

                if (isBopis) {
                    const storeAddress = basket?.shipments?.[0]?.shippingAddress
                    customerShippingDetails = getCustomerShippingDetails(
                        shippingContact,
                        isBopis,
                        storeAddress
                    )
                }

                basketResponse = await adyenShippingAddressService.updateShippingAddress(
                    basketResponse.basketId,
                    customerShippingDetails
                )

                const {applicableShippingMethods} = await fetchShippingMethods(
                    basketResponse?.basketId,
                    site,
                    authToken,
                    isBopis
                )

                if (!applicableShippingMethods?.length) {
                    reject()
                } else {
                    let applicableShippingMethod = isBopis
                        ? applicableShippingMethods?.find(
                              ({c_storePickupEnabled}) => c_storePickupEnabled
                          )
                        : applicableShippingMethods?.find(
                              ({id}) => id === basketResponse?.shipments?.[0]?.shippingMethod?.id
                          )
                    applicableShippingMethod =
                        applicableShippingMethod || applicableShippingMethods[0]
                    /** Select Two Day method if it need **/
                    let forceMethodId = ''
                    const isTwoDayApplicable = Boolean(
                        applicableShippingMethods?.some((method) => method.id === TWO_DAY_METHOD_ID)
                    )
                    const fineJewelryExists = isFineJewelryExists(window.pwaProducts)
                    if (
                        localStorage.getItem('customerSelectedShippingMethod') !== 'true' &&
                        !localStorage.getItem('2DayApplied') &&
                        fineJewelryExists &&
                        !isBopis &&
                        // Only force Two-Day where it can actually ship; on APO/FPO/US-territory
                        // addresses it isn't applicable and the shipment should default to Overseas.
                        isTwoDayApplicable
                    ) {
                        forceMethodId = TWO_DAY_METHOD_ID
                        localStorage.removeItem('customerSelectedShippingMethod')
                        localStorage.setItem('2DayApplied', true)
                    }
                    /** Select Two Day method if it need **/

                    basketResponse = await adyenShippingMethodsService.updateShippingMethod(
                        forceMethodId || applicableShippingMethod.id,
                        basketResponse.basketId
                    )
                    buttonConfig.amount = {
                        value: getCurrencyValueForApi(
                            basketResponse.orderTotal,
                            basketResponse.currency
                        ),
                        currency: basketResponse.currency,
                        shippingMethods: applicableShippingMethods
                    }
                    applePayAmount = basketResponse.orderTotal
                    const finalPriceUpdate = {
                        newShippingMethods: [...applicableShippingMethods]
                            .sort((a, b) => {
                                if (
                                    a.id === forceMethodId ||
                                    a.id === applicableShippingMethod?.id
                                ) {
                                    return -1
                                } else if (
                                    b.id === forceMethodId ||
                                    b.id === applicableShippingMethod?.id
                                ) {
                                    return 1
                                }
                                return 0
                            })
                            .map((sm) => ({
                                label: sm.name,
                                detail: sm.description,
                                identifier: sm.id,
                                amount: `${sm.price}`
                            })),
                        newTotal: {
                            type: 'final',
                            label: applePayConfig.merchantName,
                            amount: `${applePayAmount}`
                        }
                    }
                    resolve(finalPriceUpdate)
                }
            } catch (err) {
                reject()
            }
        },
        onShippingMethodSelected: async (resolve, reject, event) => {
            try {
                const {shippingMethod} = event
                const adyenShippingMethodsService = new AdyenShippingMethodsService(authToken, site)
                const response = await adyenShippingMethodsService.updateShippingMethod(
                    shippingMethod.identifier,
                    basket.basketId
                )
                if (response.error) {
                    reject()
                } else {
                    buttonConfig.amount = {
                        value: getCurrencyValueForApi(response.orderTotal, response.currency),
                        currency: response.currency
                    }
                    applePayAmount = response.orderTotal
                    const applePayShippingMethodUpdate = {
                        newTotal: {
                            type: 'final',
                            label: applePayConfig.merchantName,
                            amount: `${applePayAmount}`
                        }
                    }
                    resolve(applePayShippingMethodUpdate)
                }
            } catch (err) {
                reject()
            }
        }
    }
    return buttonConfig
}

const ApplePayExpressComponent = (props) => {
    const {setApplePayApplicable} = props
    const {
        adyenEnvironment,
        adyenPaymentMethods,
        basket,
        locale,
        site,
        authToken,
        navigate,
        shippingMethods,
        fetchShippingMethods,
        isBopis
    } = props.isCheckout ? useAdyenCheckout() : useAdyenExpressCheckout() // eslint-disable-line react-hooks/rules-of-hooks
    const tagManager = useTagManager()
    const {isCheckout} = props

    const paymentContainer = useRef(null)

    const [applePayAvailable, setApplePayAvailable] = useState(false)
    const {store} = useStoreContext()

    useEffect(() => {
        const createCheckout = async () => {
            try {
                const checkout = await AdyenCheckout({
                    environment: adyenEnvironment?.ADYEN_ENVIRONMENT,
                    clientKey: adyenEnvironment?.ADYEN_CLIENT_KEY,
                    locale: locale.id,
                    analytics: {
                        analyticsData: {
                            applicationInfo: adyenPaymentMethods?.applicationInfo
                        }
                    },
                    paymentMethodsConfiguration: {
                        applepay: {
                            buttonType: 'plain',
                            buttonColor: 'white',
                            onClick: (resolve) => {
                                if (!isCheckout) {
                                    tagManager.paymentMethodSelection('applepay_express')
                                }
                                resolve()
                            }
                        }
                    }
                })
                const applePaymentMethodConfig = getApplePaymentMethodConfig(adyenPaymentMethods)
                const appleButtonConfig = getAppleButtonConfig(
                    authToken,
                    site,
                    basket,
                    shippingMethods?.applicableShippingMethods,
                    applePaymentMethodConfig,
                    navigate,
                    fetchShippingMethods,
                    isBopis,
                    store
                )
                const applePayButton = await checkout.create('applepay', appleButtonConfig)
                window.applePayButton = applePayButton
                const isApplePayButtonAvailable = await applePayButton.isAvailable()
                if (isApplePayButtonAvailable) {
                    applePayButton.mount(paymentContainer.current)
                }
                if (setApplePayApplicable) {
                    setApplePayApplicable(isApplePayButtonAvailable ? true : false)
                }
            } catch (err) {
                // Error
                if (setApplePayApplicable) {
                    setApplePayApplicable(false)
                }
                console.warn(err.message)
            }
        }

        if (
            adyenEnvironment &&
            adyenPaymentMethods &&
            basket &&
            shippingMethods &&
            paymentContainer.current
        ) {
            createCheckout()
        }
    }, [
        adyenEnvironment,
        adyenPaymentMethods,
        shippingMethods,
        isBopis,
        basket,
        paymentContainer.current
    ])

    useEffect(() => {
        const interval = setInterval(() => {
            if (window.applePayButton) {
                window.applePayButton
                    .isAvailable()
                    .then((available) => {
                        if (available) {
                            setApplePayAvailable(true)
                        }
                    })
                    .catch((err) => {
                        console.error(err)
                        setApplePayAvailable(false)
                    })

                clearInterval(interval)
            }
        }, 100)
        return () => clearInterval(interval)
    }, [])

    return (
        <>
            {props.showLoading && (
                <Flex align={'center'} justify={'center'}>
                    <Spinner size={'lg'} mt={4} />
                </Flex>
            )}
            <Box
                sx={{
                    '.adyen-checkout__applepay__button': {
                        flex: '1',
                        width: '100%',
                        height: '50px',
                        borderRadius: '10px',
                        '-apple-pay-button-style': 'white-outline'
                    },
                    '.apple-pay-wrapper': {
                        flex: '1',
                        height: '50px'
                    }
                }}
                className="apple-pay-wrapper"
                ref={paymentContainer}
                style={{display: applePayAvailable ? 'block' : 'none'}}
            ></Box>
        </>
    )
}

ApplePayExpressComponent.propTypes = {
    showLoading: PropTypes.bool,
    shippingMethods: PropTypes.array,
    setApplePayApplicable: PropTypes.any,
    isCheckout: PropTypes.bool
}

export default ApplePayExpressComponent
